import { action, makeObservable, observable } from 'mobx';

import { EnumOptions, FormActionsType, FormActionType } from 'utils/api/modules/entities/entities.types';

import FormBuilder from '../FormBuilder';

// FORM ACTIONS - conditional logic for submitting the form

export const NEXT_SECTION = 'next';
export const SUBMIT_SECTION = 'submit';

class FormAction {
  @observable formActions: { [sectionKey: string]: FormActionsType } = {};

  constructor(private formBuilder: FormBuilder) {
    makeObservable(this);
  }

  // Helper function to create a new action
  createNewAction(when: FormActionType['when'], then: FormActionType['then']): FormActionType {
    return { when, then };
  }

  // Helper function to filter actions by question key
  filterActionsByQuestionKey(actions: FormActionType[], questionKey: string): FormActionType[] {
    return actions.filter(({ when }) =>
      typeof when === 'object' && 'equals' in when ? when.equals[0] === questionKey : false
    );
  }

  // Helper function to remove actions by question key
  removeActionsByQuestionKey(actions: FormActionType[], questionKey: string): FormActionType[] {
    if (!actions) {
      return [];
    }
    return actions.filter(({ when }) => when !== true && typeof when === 'object' && when.equals[0] !== questionKey);
  }

  // remove actions by dropdown option
  removeActionsByDropdownOption(actions: FormActionType[], dropdownOption: string): FormActionType[] {
    return actions.filter(
      ({ when }) => when !== true && typeof when === 'object' && when.equals[1][0] !== dropdownOption
    );
  }

  // Helper function to remove actions by section key
  removeActionsBySectionKey(actions: FormActionType[], sectionKey: string): FormActionType[] {
    return actions.filter((ac) => ('nextSection' in ac.then ? ac.then.nextSection !== sectionKey : true));
  }

  // Helper function to remove actions by section key
  removeActionsWithOptionKey(sectionId: string, optionKey: string): void {
    const actions = this.formActions[sectionId];
    if (!actions) {
      return;
    }

    actions.forEach((a) => {
      if (typeof a.when === 'object' && a.when.equals[1][0] === optionKey) {
        this.formActions[sectionId] = this.formActions[sectionId].filter(
          (ac) => typeof ac.when === 'object' && ac.when.equals[1][0] !== optionKey
        );
      }
    });
  }

  // get the default value for a section
  @action.bound
  getSectionDefaultAction(sectionKey: string): string {
    if (!this.formActions) {
      return NEXT_SECTION;
    }

    const defaultAction = this.formActions[sectionKey]?.find((ac) => ac.when === true);

    if (defaultAction) {
      if ('nextSection' in defaultAction.then) {
        // if this is the next section, return next
        if (
          defaultAction.then.nextSection ===
          this.formBuilder.containers[this.formBuilder.containers.indexOf(sectionKey) + 1]
        ) {
          return NEXT_SECTION;
        }
        return defaultAction.then.nextSection;
      }
      return SUBMIT_SECTION;
    }

    return NEXT_SECTION;
  }

  // set the default action for a section
  @action.bound
  setSectionDefaultAction(sectionKey: string, nextSection: string): void {
    if (!this.formActions) {
      this.formActions = {};
    }

    const nextSectionId =
      nextSection === NEXT_SECTION
        ? this.formBuilder.containers[this.formBuilder.containers.indexOf(sectionKey) + 1]
        : nextSection;

    const newAction: FormActionType = {
      when: true,
      then: nextSection === SUBMIT_SECTION || !nextSectionId ? { submit: true } : { nextSection: nextSectionId },
    };

    const existingDefaultAction = this.formActions[sectionKey]?.find((ac) => ac.when === true);
    if (existingDefaultAction) {
      this.formActions[sectionKey] = this.formActions[sectionKey].filter((ac) => ac.when !== true);
      this.formActions[sectionKey].push(newAction);
    } else if (this.formActions[sectionKey]) {
      this.formActions[sectionKey].push(newAction);
    } else {
      this.formActions[sectionKey] = [newAction];
    }

    // Force MobX to detect the change by creating a new reference
    this.formActions = { ...this.formActions };
  }

  @action.bound
  duplicateQuestionActions(sectionKey: string, questionKey: string, newQuestionKey: string): void {
    if (!this.formActions) {
      return;
    }

    const sectionAction = this.formActions[sectionKey];
    if (!sectionAction) {
      return;
    }

    const actions = this.filterActionsByQuestionKey(sectionAction, questionKey);
    if (!actions.length) {
      return;
    }

    actions.forEach(({ when, then }) => {
      if (typeof when === 'boolean') {
        return;
      }

      const newWhen: [string, [string]] = [newQuestionKey, when.equals[1]];
      const newAction = this.createNewAction({ equals: newWhen }, then);

      this.formActions[sectionKey].push(newAction);
    });
  }

  @action.bound
  removeQuestionActions(sectionKey: string, questionKey: string): void {
    if (!this.formActions) {
      return;
    }

    this.formActions[sectionKey] = this.removeActionsByQuestionKey(this.formActions[sectionKey], questionKey);
  }

  @action.bound
  removeDefaultSectionAction(sectionKey: string): void {
    if (!this.formActions) {
      return;
    }

    Object.keys(this.formActions).forEach((key) => {
      if (
        this.formActions[key].find(
          (ac) => ac.when === true && (ac.then as { nextSection: string }).nextSection === sectionKey
        )
      ) {
        this.setSectionDefaultAction(key, NEXT_SECTION);
      }
    });
  }

  @action.bound
  removeSectionActions(sectionKey: string): void {
    if (!this.formActions) {
      return;
    }

    delete this.formActions[sectionKey];

    Object.keys(this.formActions).forEach((key) => {
      this.formActions[key] = this.removeActionsBySectionKey(this.formActions[key], sectionKey);
    });
  }

  // - moving question from one section to another - update the actions = move actions from one section to another
  @action.bound
  moveQuestionActions(activeSectionKey: string, overSectionKey: string, questionKey: string): void {
    if (!this.formActions) {
      return;
    }

    const activeSection = this.formActions[activeSectionKey];

    if (!activeSection) {
      return;
    }

    const activeActions = activeSection.filter(
      ({ when }) => typeof when === 'object' && when.equals[0] === questionKey
    );

    if (!activeActions) {
      return;
    }

    activeActions.forEach(({ when, then }) => {
      const value = typeof when === 'object' ? when.equals[1][0] : '';
      const newAction: FormActionType = {
        when: {
          equals: [questionKey, [value]],
        },
        then,
      };

      if (this.formActions[overSectionKey]) {
        this.formActions[overSectionKey].push(newAction);
      } else {
        this.formActions[overSectionKey] = [newAction];
      }
    });

    this.formActions[activeSectionKey] = activeSection.filter(
      ({ when }) => typeof when === 'object' && when.equals[0] !== questionKey
    );
  }

  @action.bound
  updateFormActions(sectionKey: string, questionKey: string, questionValue: string, nextSection: string): void {
    if (!this.formActions) {
      this.formActions = {};
    }

    // Get existing actions or initialize new array
    const actions = this.formActions[sectionKey] || [];

    // Find existing action for this question/value combination
    const actionIndex = actions.findIndex(
      ({ when }) =>
        when !== true &&
        typeof when === 'object' &&
        'equals' in when &&
        when.equals[0] === questionKey &&
        when.equals[1][0] === questionValue
    );

    // Handle "next" section logic
    let targetSection: string = nextSection;
    if (nextSection === NEXT_SECTION) {
      const { containers } = this.formBuilder;
      if (!containers || !containers.length) {
        targetSection = SUBMIT_SECTION;
      } else {
        const currentIndex = containers.indexOf(sectionKey);
        targetSection =
          currentIndex >= 0 && currentIndex < containers.length - 1 ? containers[currentIndex + 1] : SUBMIT_SECTION;
      }
    }

    // Create new action based on nextSection value
    const newAction: FormActionType = {
      when: {
        equals: [questionKey, [questionValue]],
      },
      then: nextSection === SUBMIT_SECTION ? { submit: true } : { nextSection: targetSection },
    };

    // Update or add the action
    if (actionIndex >= 0) {
      this.formActions[sectionKey][actionIndex] = newAction;
    } else {
      if (!this.formActions[sectionKey]) {
        this.formActions[sectionKey] = [];
      }
      this.formActions[sectionKey].push(newAction);
    }
  }

  getActionsForQuestion = (sectionKey: string, questionKey: string): FormActionsType => {
    if (!this.formActions) {
      return [];
    }
    return (
      this.formActions[sectionKey]?.filter(({ when }) => typeof when === 'object' && when.equals[0] === questionKey) ||
      []
    );
  };

  getActionForQuestionAndValue = (sectionKey: string, questionKey: string, questionValue: string): string => {
    if (!this.formActions) {
      return '';
    }

    const value = this.formActions[sectionKey]?.find(
      ({ when }) =>
        typeof when === 'object' &&
        'equals' in when &&
        when.equals[0] === questionKey &&
        when.equals[1][0] === questionValue
    );

    // if the value.then.nextSection is the key of the next section in the order, return `next`
    const nextSectionKey = this.formBuilder.containers[this.formBuilder.containers.indexOf(sectionKey) + 1];

    if (value === undefined) {
      return NEXT_SECTION;
    }

    if (value && 'nextSection' in value.then && value.then.nextSection === nextSectionKey) {
      return NEXT_SECTION;
    }

    if (value && SUBMIT_SECTION in value.then) {
      return SUBMIT_SECTION;
    }

    return (value && 'nextSection' in value.then && value?.then.nextSection) || nextSectionKey;
  };

  @action.bound
  createDefaultActionForQuestion(sectionKey: string, questionKey: string): void {
    if (!this.formActions) {
      return;
    }

    // default section should always redirect to next section or submit
    const nextSectionKey = this.formBuilder.containers[this.formBuilder.containers.indexOf(sectionKey) + 1];

    const questionValues = this.formBuilder.data?.properties[sectionKey].properties[questionKey].properties
      .enum as unknown as EnumOptions;

    if (questionValues) {
      questionValues.forEach((value) => {
        const newAction: FormActionType = {
          when: {
            equals: [questionKey, [value.key]],
          },
          then: { nextSection: nextSectionKey },
        };

        if (this.formActions[sectionKey]) {
          this.formActions[sectionKey].push(newAction);
        } else {
          this.formActions[sectionKey] = [newAction];
        }
      });
    }
  }
}

export default FormAction;
